import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, MapPin, Tag, Check, Phone } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { PROJECTS } from "@/data/references";
import { usePageMeta } from "@/utils/seo";

export default function ReferenzDetail() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  const [activeImg, setActiveImg] = useState(project?.img);
  usePageMeta(project && {
    title: `${project.title} | Referenz ${project.service} | HRG Service GmbH`,
    description: `${project.service} — Referenzprojekt der HRG Service GmbH in ${project.location}. ${project.details[0]}.`,
    path: `/referenzen/${project.slug}`,
  });

  if (!project) return <Navigate to="/referenzen" replace />;
  const current = activeImg || project.img;

  return (
    <main className="pt-20" data-testid="reference-detail-page">
      <section className="bg-mesh border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
          <Link
            to="/referenzen"
            data-testid="reference-back-link"
            className="group mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-[#3B62A5]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
            Alle Referenzen
          </Link>
          <p className="font-tech text-xs uppercase tracking-[0.3em] text-[#3B62A5] font-semibold mb-5">
            {project.category} — {project.since}
          </p>
          <h1 data-testid="reference-detail-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A111E] leading-[1.08]">
            <MaskedLines lines={[project.title]} />
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#3B62A5]" />{project.location}</span>
              <span className="flex items-center gap-1.5"><Tag className="h-4 w-4 text-[#3B62A5]" />{project.service}</span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="glass-card overflow-hidden rounded-3xl">
                <motion.img
                  key={current}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  src={current}
                  alt={project.title}
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                  data-testid="reference-detail-main-image"
                />
              </div>
              {project.gallery?.length > 1 && (
                <div data-testid="reference-detail-gallery" className="mt-4 grid grid-cols-5 sm:grid-cols-9 gap-2">
                  {project.gallery.map((g, i) => (
                    <button
                      key={g}
                      onClick={() => setActiveImg(g)}
                      data-testid={`reference-gallery-thumb-${i}`}
                      aria-label={`Bild ${i + 1} anzeigen`}
                      className={`overflow-hidden rounded-xl border-2 transition-colors duration-200 ${current === g ? "border-[#3B62A5]" : "border-transparent hover:border-slate-300"}`}
                    >
                      <img src={g} alt={`${project.title} — Bild ${i + 1}`} loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Reveal>
          </div>

          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <h2 className="font-tech text-xs uppercase tracking-[0.25em] text-[#3B62A5] font-semibold mb-5">Leistungen in diesem Projekt</h2>
              <ul className="space-y-3">
                {project.details.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center bg-[#E8F0FE]"><Check className="h-3.5 w-3.5 text-[#3B62A5]" /></span>
                    {d}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 rounded-3xl border border-white/20 bg-gradient-to-br from-[#3B62A5] to-[#2A477A] p-7 text-white shadow-[0_20px_50px_rgba(59,98,165,0.25)]">
                <p className="text-lg font-semibold leading-snug">Ähnliches Projekt geplant?</p>
                <p className="mt-2 text-sm text-white/80 leading-relaxed">Wir erstellen Ihnen gerne eine unverbindliche Offerte.</p>
                <Link
                  to="/kontakt?service=spezialreinigung"
                  data-testid="reference-detail-cta"
                  className="mt-5 inline-flex items-center gap-2 bg-white px-5 py-3 text-sm font-semibold text-[#3B62A5] transition-colors duration-200 hover:bg-[#E8F0FE]"
                >
                  Offerte anfragen <ArrowUpRight className="h-4 w-4" />
                </Link>
                <a href="tel:+41782238087" className="mt-4 flex items-center gap-2 text-sm text-white/80 transition-colors duration-200 hover:text-white" data-testid="reference-detail-phone">
                  <Phone className="h-4 w-4" /> +41 78 223 80 87
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
